import { computed, getCurrentInstance, inject, nextTick, unref } from 'vue';

import { attachedRoot, IN_BROWSER, warn } from '@components/utils';

import type { Ref, ComputedRef, InjectionKey } from 'vue';

export interface IUseTeleport {
  teleportTarget: ComputedRef<Element | undefined>;
}

export interface ITeleportOptions {
  /**
   * Указывает, к какому DOM элементу будут прикреплены компоненты использующие телепорт.
   * По-умолчанию прикрепляется к body.
   */
  target: Element;
  /**
   * Указывает, что все всплывающие компоненты будут прикреплены к родительскому элементу-активатору автоматически.
   * Игнорирует переданный target во всех случаях.
   * ВНИМАНИЕ! Возможны проблемы с позиционированием элементов вложенных в другие плавающие элементы в DOM.
   * Используйте только если вы уверены, что это необходимо. В противном случае используйте свойства attach и contained непосредственно в самих компонентах.
   */
  contained: boolean;
  /**
   * Указывает, что все всплывающие компоненты будут прикреплены к первому элементу в getRootNode автоматически.
   * Игнорирует переданный target во всех случаях.
   */
  root: boolean;
  /**
   * Класс контейнера для компонентов телепорта.
   */
  containerClassName: string;
}

export const TeleportSymbol: InjectionKey<ITeleportOptions> = Symbol('cds:teleport');

export function useTeleport(target: Ref<boolean | string | Element>, overlayContainerClassName: string): IUseTeleport {
  const vm = getCurrentInstance();

  const teleportTarget = computed(() => {
    if (!IN_BROWSER) return undefined;

    const defaultValue = {
      target: document.body,
      contained: false,
      root: false,
      containerClassName: overlayContainerClassName
    };

    const teleportConfig = { ...defaultValue, ...inject(TeleportSymbol, { ...defaultValue }) };
    const _target = teleportConfig.contained || unref(target);

    if (_target === true) return undefined;

    const targetElement =
      _target === false
        ? teleportConfig.target
        : typeof _target === 'string'
        ? document.querySelector(_target)
        : _target;

    if (targetElement == null) {
      warn('teleport', `Unable to locate target element: ${_target}`);
      return undefined;
    }

    let container = targetElement.querySelector(`:scope > .${teleportConfig.containerClassName}`);

    if (!container) {
      container = document.createElement('div');
      container.className = teleportConfig.containerClassName;

      if (teleportConfig.root) {
        nextTick(() => {
          const root = attachedRoot(vm?.proxy?.$el);
          root?.lastElementChild?.appendChild(container!);
        });
      } else {
        targetElement.appendChild(container);
      }
    }

    return container;
  });

  return { teleportTarget };
}
